import { FC, FocusEvent, KeyboardEvent, memo, useCallback, useRef, useState } from 'react'
import { DraggableProvided } from 'react-beautiful-dnd'
import { getCaretPosition, placeCaret, Position, sanitizeCell } from '../Utilities/tools'
import VocabularyTools from './VocabularyTools'
import { IVocabulary, IVocabularyRow } from '../Types/VocabularyTypes'
import { useAppSelector } from '../hooks'
import SpellcheckedEditor from './SpellcheckedEditor'
import { useVocabularyActions } from '../hooks/useVocabularyActions'

interface VocabularyRowProps {
  row: IVocabularyRow
  index: number
  onFocus: (index: number, column: 'left' | 'right') => void
  vocabulary: IVocabulary
  readOnly: boolean
  displayTools: string
  provided: DraggableProvided
  isDragging: boolean
  navigateToRow: (targetIndex: number, columnClass: string, caretPos: Position) => void
}

// Memoized Row component
const VocabularyRow: FC<VocabularyRowProps> = (
  {
    row,
    index,
    onFocus,
    vocabulary,
    readOnly,
    displayTools,
    provided,
    isDragging,
    navigateToRow,
  }) => {
  // console.log('Rendering row', index)
  const lastFocusedRow = useAppSelector(state => state.vocabularies.lastFocusedRow)
  const highlightedRows = useAppSelector(state => state.vocabularies.highlightedRows)
  const isHighlighted = highlightedRows.includes(index)

  // Add refs to track original content before edits
  const originalLeftContent = useRef(sanitizeCell(row.left))
  const originalRightContent = useRef(sanitizeCell(row.right))

  const [focusedCell, setFocusedCell] = useState<null | 'left' | 'right'>(null)
  const [leftValue, setLeftValue] = useState(sanitizeCell(row.left))
  const [rightValue, setRightValue] = useState(sanitizeCell(row.right))

  const { handleVocabularyChange } = useVocabularyActions(vocabulary, { index })

  const handleCellEdit = useCallback(
    (column: 'left' | 'right', newValue: string) => {
      const formattedValue = newValue.replace(/\/\//g, '<br>')
      if (formattedValue === row[column]) return

      const updatedRow = { ...row }
      updatedRow[column] = formattedValue

      handleVocabularyChange({ operation: 'updateRow', position: index, row: updatedRow })
    }, [row, index, handleVocabularyChange],
  )

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, columnClass: string) => {
    console.log('handleKeyDown', e.key, columnClass)
    // Handle arrow up and down keys and Enter for row navigation
    if (['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
      e.preventDefault()
      const caretPos = (getCaretPosition(e.currentTarget) === 'end') ? 'end' : 'start'
      if ((e.key === 'ArrowUp' || (e.key === 'Enter' && e.shiftKey)) && index > 0) {
        navigateToRow(index - 1, columnClass, caretPos)
      } else if (['ArrowDown', 'Enter'].includes(e.key)) {
        navigateToRow(index + 1, columnClass, caretPos)
      }
    }

    // Handle left/right navigation between cells
    else if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
      const caretPos = window.getSelection()?.getRangeAt(0).startOffset
      const textLength = e.currentTarget.textContent?.length

      if (e.key === 'ArrowLeft' && caretPos === 0) {
        if (columnClass === 'right') {
          // Move to the left cell in the same row
          const leftCell = e.currentTarget.parentElement?.querySelector('.td.left') as HTMLElement
          if (leftCell) {
            e.preventDefault()
            placeCaret(leftCell, 'end')
          }
        } else if (index > 0) {
          // Move to the right cell of the previous row
          e.preventDefault()
          navigateToRow(index - 1, 'right', 'end')
        }
      } else if (e.key === 'ArrowRight' && caretPos === textLength) {
        if (columnClass === 'left') {
          // Move to the right cell in the same row
          const rightCell = e.currentTarget.parentElement?.querySelector('.td.right') as HTMLElement
          if (rightCell) {
            e.preventDefault()
            rightCell.focus()
          }
        } else {
          // Move to the source cell of the next row
          e.preventDefault()
          navigateToRow(index + 1, 'left', 'start')
        }
      }
    }
    // Handle tab navigation
    else if (e.key == 'Tab') {
      if (e.shiftKey) {
        if (columnClass === 'left') {
          if (index === 0) {
            return
          }
          e.preventDefault()
          navigateToRow(index - 1, 'right', 'end')
        } else {
          e.preventDefault()
          navigateToRow(index, 'left', 'end')
        }
      } else {
        if (columnClass === 'left') {
          e.preventDefault()
          navigateToRow(index, 'right', 'end')
        } else {
          e.preventDefault()
          navigateToRow(index + 1, 'left', 'end')
        }
      }
    }
  }

  // Update refs when row content changes
  if (sanitizeCell(row.left) !== originalLeftContent.current) {
    originalLeftContent.current = sanitizeCell(row.left)
  }
  if (sanitizeCell(row.right) !== originalRightContent.current) {
    originalRightContent.current = sanitizeCell(row.right)
  }

  const handleLeftFocus = () => {
    setFocusedCell('left')
    onFocus(index, 'left')
  }

  const handleRightFocus = () => {
    setFocusedCell('right')
    onFocus(index, 'right')
  }

  const handleLeftEdit = (newValue: string) => {
    handleCellEdit('left', newValue)
    originalLeftContent.current = newValue
  }
  const handleLeftBlur = (e: FocusEvent<HTMLDivElement>) => {
    setFocusedCell(null)
    const newValue = sanitizeCell(e.target.innerHTML)
    if (newValue !== originalLeftContent.current) {
      handleCellEdit('left', newValue)
      originalLeftContent.current = newValue
    }
  }
  const handleRightBlur = (e: FocusEvent<HTMLDivElement>) => {
    setFocusedCell(null)
    const newValue = sanitizeCell(e.target.innerHTML)
    if (newValue !== originalRightContent.current) {
      handleCellEdit('right', newValue)
      originalRightContent.current = newValue
    }
  }

  const handleLeftInput = (newValue: string) => {
    setLeftValue(newValue)
  }

  const handleRightInput = (newValue: string) => {
    setRightValue(newValue)
  }

  return (
    <div
      className={`tr ${index === lastFocusedRow ? 'last-selected' : ''} ${isDragging ? 'dragging' : ''} ${isHighlighted ? 'highlighted' : ''}`}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      <div className="td selector" {...provided.dragHandleProps} tabIndex={-1}>
        <div className="grab"></div>
      </div>
      <div className={'td left' + (focusedCell === 'left' ? ' focused' : '')}>
        <div className="row">
          <SpellcheckedEditor
            value={row.left || ''}
            onChange={handleLeftEdit}
            onInput={handleLeftInput}
            language={vocabulary.left_language}
            onFocus={handleLeftFocus}
            onBlur={handleLeftBlur}
            onKeyDown={(e) => handleKeyDown(e, 'left')}
            readOnly={readOnly}
            className="input"
          />
          <VocabularyTools
            rowIndex={index}
            rowInput={{left: leftValue, right: rightValue}}
            side="left"
            vocabulary={vocabulary}
            readOnly={readOnly}
            displayTools={displayTools}
            onTranslationResult={(translated) => handleCellEdit('left', translated)}
          />
        </div>
      </div>
      <div className={'td right' + (focusedCell === 'right' ? ' focused' : '')}>
        <div className="row">
          <div
            className="input"
            lang={vocabulary.right_language}
            contentEditable={!readOnly}
            suppressContentEditableWarning
            onFocus={handleRightFocus}
            onBlur={handleRightBlur}
            onKeyDown={(e) => handleKeyDown(e, 'right')}
            onInput={(e) => handleRightInput(e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: sanitizeCell(row.right) || '' }}
          />
          <VocabularyTools
            rowIndex={index}
            rowInput={{left: leftValue, right: rightValue}}
            side="right"
            vocabulary={vocabulary}
            readOnly={readOnly}
            displayTools={displayTools}
            onTranslationResult={(translated) => handleCellEdit('right', translated)}
          />
        </div>
      </div>
    </div>
  )
}

export default memo(VocabularyRow)